import { useMemo } from 'react'

export interface MessageFormattingResult {
  formattedContent: string
  isMarkdown: boolean
  hasCodeBlocks: boolean
  hasLinks: boolean
}

/**
 * Hook for analyzing and formatting message content
 *
 * Detects:
 * - Markdown formatting (bold, italic, lists, etc.)
 * - Code blocks (inline and fenced)
 * - Links and URLs
 *
 * Used to determine appropriate rendering strategy
 */
export const useMessageFormatting = (
  content: string
): MessageFormattingResult => {
  const formatting = useMemo(() => {
    const hasCodeBlocks = content.includes('```') || content.includes('`')
    const hasLinks = /\[.+\]\(.+\)/.test(content) || /https?:\/\//.test(content)
    const hasMarkdownSyntax =
      content.includes('**') ||
      content.includes('*') ||
      content.includes('#') ||
      content.includes('- ') ||
      content.includes('1. ') ||
      hasCodeBlocks ||
      hasLinks

    return {
      formattedContent: content,
      isMarkdown: hasMarkdownSyntax,
      hasCodeBlocks,
      hasLinks,
    }
  }, [content])

  return formatting
}
